import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/shell/PageHeader';
import { menuIcons } from '../components/shell/menuIcons';
import { SettingsTabs, type SettingsTab } from '../components/settings/SettingsTabs';
import { PlatformSettingsCard } from '../features/approval/PlatformSettingsCard';
import { EntraGroupsCard } from '../features/entra/EntraGroupsCard';
import { MembersCard } from '../features/members/MembersCard';
import { AiLengthGuidelinesCard } from '../features/settings/AiLengthGuidelinesCard';
import { CustomerDirectoryCard } from '../features/settings/CustomerDirectoryCard';
import { AiModelsCard } from '../features/settings/AiModelsCard';
import { useIsTenantAdmin, useIsWorkspaceAdmin } from '../auth/useWorkspaceAdmin';
import { api } from '../lib/api';
import { env } from '../lib/env';

export function AppSettingsPage() {
  // App Settings RBAC: a workspace admin (Tenant Admin or WORKSPACE_ADMIN) configures
  // the workspace + members; everyone else may only VIEW the (read-only) Microsoft 365
  // group + member lists so they know who has access. The corporate integrations live on
  // their own Tenant-Admin-only Site Integrations page. Install-wide AI model choices are
  // here too, for Tenant Admins only.
  //
  // Personal Voice used to be here too. It moved to My Profile: it is a property of the
  // person, not of the workspace, and sitting among workspace settings invited the reading
  // that it was shared. Company Voice is still a workspace setting and is unaffected.
  const isAdmin = useIsWorkspaceAdmin();

  // Which directory this install's customer signs in from, so the Microsoft-only cards can stand
  // down on a Google Workspace install — Microsoft 365 groups are not a thing there, and the list
  // would be permanently empty (a customer install is deliberately not granted the Graph roles
  // that read a directory).
  //
  // The STORED choice, shared with CustomerDirectoryCard through the same query key, NOT the
  // `env.customerDirectory` that /config.js serves. Two reasons: /config.js reports 'google' only
  // once Google sign-in would actually work, so the card would linger until a client id and
  // domains were filled in; and the save writes this cache entry directly, so picking Google makes
  // the card disappear at once rather than after a reload.
  //
  // `env.customerDirectory` is the fallback for a non-Tenant-Admin, whose query is disabled (the
  // endpoint 403s for them). That covers the real cases: on a configured Google install it already
  // reads 'google', and a half-configured one cannot sign a customer in at all, so the only people
  // there are administrators, who are Tenant Admins.
  const isTenantAdmin = useIsTenantAdmin();
  const directory = useQuery({
    queryKey: ['customer-directory'],
    queryFn: ({ signal }) => api.getCustomerDirectory(signal),
    enabled: isTenantAdmin,
  });
  const isGoogleDirectory = (directory.data?.directory ?? env.customerDirectory) === 'google';

  // The page's sections as tabs (spec "App Settings, Billing Admin and Site Integrations"), in page
  // order. A tab is shown by the same rule that shows its card, and the first one shown opens by
  // default. The sticky Save footer is on the tabs with fields THIS person can save: a Tenant Admin's
  // first four. Everyone else can only read those (or does not see them), so no tab has a footer for
  // them. Microsoft 365 Groups and Members act at once (Register group, Remove, access and role
  // changes, Make and Revoke Tenant Admin, Add member, Invite approver): no footer there.
  const tabs: SettingsTab[] = [
    {
      // First: which directory this install's customer signs in from. It is the thing an Alloterra
      // administrator comes to this page to set when standing a customer up. Tenant Admins only: the
      // card draws nothing for anyone else.
      key: 'sign-in',
      label: 'Customer sign-in',
      visible: isTenantAdmin,
      footer: isTenantAdmin,
      render: () => <CustomerDirectoryCard />,
    },
    {
      // Prompt tuning: this shapes HOW LONG the AI writes. Install-wide, so it's shown to everyone
      // and gated to Tenant Admins internally.
      key: 'content-length',
      label: 'AI Assist content length',
      footer: isTenantAdmin,
      render: () => <AiLengthGuidelinesCard />,
    },
    {
      // Which Azure AI deployment each AI feature uses on this install. Tenant-Admin-only and hidden
      // from everyone else (its endpoints 403 for them); install-wide, so no workspace is needed. Its
      // Refresh list, "Build requests for" and Discard changes stay in the card.
      key: 'ai-models',
      label: 'AI models',
      visible: isTenantAdmin,
      footer: isTenantAdmin,
      render: () => <AiModelsCard />,
    },
    {
      // Install-wide approval policy: shown to everyone so members can see the policy; the card
      // itself makes the controls read-only for non-Tenant-Admins.
      key: 'approvals',
      label: 'Approval settings (all workspaces)',
      footer: isTenantAdmin,
      render: () => <PlatformSettingsCard />,
    },
    {
      // Group + member lists: visible to everyone, but read-only for non-admins (their own isAdmin
      // gating hides the register/invite/edit/remove controls). The Members card now also carries
      // the "invite from a Microsoft 365 group" add-member flow.
      //
      // Microsoft 365 Groups is hidden outright on a Google Workspace install: it does not apply
      // there. The group picker inside Members needs no equivalent switch — it already renders only
      // when groups exist, and on such an install there are none.
      key: 'm365-groups',
      label: 'Microsoft 365 Groups',
      visible: !isGoogleDirectory,
      render: () => <EntraGroupsCard />,
    },
    { key: 'members', label: 'Members', render: () => <MembersCard /> },
  ];

  return (
    <>
      <PageHeader
        title="App Settings"
        icon={menuIcons.appSettings}
        description={
          isTenantAdmin
            ? 'Configure this install (customer sign-in, AI models and content length, approvals) and its members. (Company Voice is a workspace setting; your Personal Voice is on My Profile.)'
            : isAdmin
              ? 'Configure this workspace and its members. (Company Voice is a workspace setting; your Personal Voice is on My Profile.)'
              : 'Workspace configuration is managed by a workspace admin. Your Personal Voice is on My Profile.'
        }
      />

      <SettingsTabs label="App Settings sections" tabs={tabs} />
    </>
  );
}
